import { TaskStatusBadge } from '@dealstack/frontend/app/pages/Export/ExportDocuments/components/TaskStatusBadge.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { GetTaskRes, TaskStatus } from '@dealstack/shared/lib/generated';
import { Button, Flex, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface Props {
	tExports: TranslateFunction<I18Namespaces.EXPORTS>;
	disabledButtons: Set<string>;
	onDownload: (task: GetTaskRes) => void;
}

export const getExportDocumentsTableColumns = ({
	tExports,
	disabledButtons,
	onDownload,
}: Props): ExtendedColumnDef<GetTaskRes>[] => [
	createTextColumn({
		id: 'createdAt',
		headerText: tExports('table.date'),
		accessorFn: (row) => row.date,
		enableSorting: true,
		cell: ({ row }) => (
			<Flex direction="column">
				<Text>{formatStringDateToUI(row.original.date, DATE_FORMATS.CALENDAR_DATE)}</Text>
				<Text c="dark.0">{formatStringDateToUI(row.original.date, DATE_FORMATS.TIME)}</Text>
			</Flex>
		),
	}),
	createTextColumn({
		id: 'status',
		headerText: tExports('table.status'),
		accessorFn: (row) => row.status,
		enableSorting: true,
		cell: ({ row }) => {
			if (!row.original.status) {
				return null;
			}

			return <TaskStatusBadge status={row.original.status} />;
		},
	}),
	createTextColumn({
		id: 'programmeName',
		headerText: tExports('table.details'),
		enableSorting: true,
		accessorFn: (row) => `${tExports('table.documents_export')} ${row.programmeName}`,
	}),
	{
		id: 'action',
		header: () => <Text fw={600}>{tExports('table.actions')}</Text>,
		enableSorting: false,
		size: 10,
		cell: ({ row }) => {
			const task = row.original;
			if (task.status === TaskStatus.ERROR) {
				return null;
			}

			const isNotCompleted = task.status !== TaskStatus.DONE;
			const shouldDisable = task.fileKey ? disabledButtons.has(task.fileKey) : true;

			return (
				<Button
					variant="subtle"
					disabled={isNotCompleted || shouldDisable}
					onClick={() => onDownload(task)}
				>
					{tExports('buttons.download')}
				</Button>
			);
		},
	},
];
